import * as React from "react";
import { useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import Alert from "@material-ui/lab/Alert";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";
import ActionClose from "@material-ui/icons/Close";

// jaza-soft
import { Button, showNotification } from "jazasoft";

// local
import MuiTable from "../../../../../components/MuiTable";

import { isEmpty } from "../../../../../utils/helpers";
import { useDeepMemo, useMutation } from "../../../../../hooks";
import { InventoryStatus, QcStatus } from "../../../../../utils/types";
import { colorLightGreen, colorOrange, colorTomatoRed } from "../../../../../utils/colors";
import { getCutOrderLabel, getGroupOrderLabel } from "../../../../../helpers/orderHelper";
import { LotType } from "../../../../../constants";
import ConfirmButton from "../../../../../components/ConfirmButton";

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
            ? colorTomatoRed.dark
            : value === QcStatus.ON_HOLD
              ? colorOrange.dark
              : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000
  },
  marginTop: {
    marginTop: "1.5em"
  },
  drawerHeader: {
    display: "flex",
    width: 540,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "1.5em"
  },
  table: {
    "& td > div": {
      minWidth: 80
    }
  }
}));

const RollRemoveDrawer = props => {
  const classes = useStyles();
  const { open, order, flowInfo = {}, part, selectedRolls, rollFields, onDrawerClose, onRefresh } = props;

  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  const onSubmit = (rows, onSubmitComplete) => {
    if (!isEmpty(errorMessage)) {
      dispatch(showNotification(errorMessage, { type: "danger" }));
      return;
    }

    const orderId = flowInfo.orderId;
    const productId = flowInfo.product?.id;
    const style = flowInfo.style;
    const color = flowInfo.color;

    const options = {
      url: `rolls/deallocate`,
      method: "put",
      params: { orderId, productId, style, color, part, lotType: "Bulk" },
      data: rows.map(roll => ({
        id: roll.id,
        rollNo: roll.rollNo,
        supplierRollNo: roll.supplierRollNo,
        allocOrderId: roll.allocOrderId,
        groupOrderId: roll.groupOrderId !== 0 ? roll.groupOrderId : null,
        cutOrderId: roll.cutOrderId !== 0 ? roll.cutOrderId : null
      }))
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Rolls deallocated successfully.`));
        onRefresh && onRefresh();
        onSubmitComplete && onSubmitComplete(rows);
        onDrawerClose && onDrawerClose();
      },
      onFailure: () => onSubmitComplete && onSubmitComplete(rows)
    });
  };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "Roll No" },
      { dataKey: "qcStatus", title: "QC Status", render: renderQcStatus },
      { dataKey: "status", title: "Status" },
      { dataKey: "length", title: "Available\nLength" },
      { dataKey: "width", title: "Cuttable\nWidth" },
      { dataKey: "warpShrPercent", title: "Shrinkage %\n(Length)" },
      { dataKey: "weftShrPercent", title: "Shrinkage %\n(Width)" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "skewness", title: "Skewness %" },
      {
        dataKey: "groupOrderId",
        title: "Group Order",
        inputType: "select-input",
        disabled: row => !isEmpty(row?.cutOrderId) && row.cutOrderId !== 0,
        choices: ({ row }) =>
          !isEmpty(row.orderFlow)
            ? [
                { id: 0, name: "None" },
                { id: row.orderFlow.id, name: getGroupOrderLabel(row.orderFlow, order, ["color"]) }
              ]
            : []
      },
      {
        dataKey: "cutOrderId",
        title: "Cut Order",
        inputType: "select-input",
        choices: ({ row }) =>
          !isEmpty(row.cutOrderFlow) && (row.cutOrderFlow?.lotType === LotType.PRE_RUN || (!isEmpty(row.groupOrderId) && row.groupOrderId !== 0))
            ? [
                { id: 0, name: "None" },
                {
                  id: row.cutOrderFlow.id,
                  name: getCutOrderLabel(row.cutOrderFlow, order, ["inseam", "destination", "deliveryDate", "delMode", "flowRef", "customerFlowRef"])
                }
              ]
            : []
      }
    ];

    const fields = [...rollFields, "splitLength", "status", "groupOrderId", "cutOrderId"];
    cols = cols.filter(c => fields.includes(c.dataKey));

    return cols;
  }, [rollFields, order]);

  const rows = useDeepMemo(() => {
    if (isEmpty(selectedRolls)) return [];
    return selectedRolls.map(e => ({
      ...e,
      splitLength: e.length,
      groupOrderId: e.orderFlow?.id,
      cutOrderId: e.cutOrderFlow?.id,
      status:
        e.invStatus === InventoryStatus.ISSUED
          ? InventoryStatus.ISSUED
          : (e.plannedInLay || 0) + (e.usedInLay || 0) + (e.usedInPart || 0) > 0
            ? InventoryStatus.PLANNED
            : InventoryStatus.NOT_PLANNED
    }));
  }, [selectedRolls]);

  const errorMessage = useDeepMemo(() => {
    let message;

    const isIssued = rows.some(roll => roll.status === InventoryStatus.ISSUED);
    // const isPlanned = rows.some(roll => roll.status === InventoryStatus.PLANNED);
    if (isIssued) {
      message = `Issued rolls cannot be removed`;
    }
    // else if (isPlanned) {
    //   message = `Planned rolls cannot be removed`;
    // }
    return message;
  }, [rows]);

  const isPlanned = rows.some(roll => roll.status === InventoryStatus.PLANNED && roll.plannedInLay > 0);

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <div>
      <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">Remove Roll</Typography>
          </div>
        </div>
        <Divider />

        <div className={classes.content}>
          {!isEmpty(errorMessage) && (
            <div>
              <Alert severity="error" variant="filled">
                <Typography variant="h6">{errorMessage}</Typography>
              </Alert>
            </div>
          )}

          <div style={{ marginTop: !isEmpty(errorMessage) ? "1.5em" : 0 }}>
            <MuiTable
              tableProps={{ className: classes.table }}
              columns={columns}
              rows={rows}
              editable={isEmpty(errorMessage)}
              editing={isEmpty(errorMessage)}
              onSubmit={(rows, _, onSubmitComplete) => onSubmit(rows, onSubmitComplete)}
              handleSubmitRef={handleSubmitRef}
            />
          </div>

          <div style={{ display: "flex", flex: 1, flexGrow: 1 }}></div>

          {/* Footer */}
          <div className={classes.marginTop}>
            {isPlanned ? (
              <ConfirmButton
                label="Submit"
                variant="contained"
                color="primary"
                icon={<ActionSend />}
                confirmTitle="Are you sure?"
                confirmMessage={"Some rolls are already planned in Roll Plan. Confirm you are intentionally removing rolls already planned"}
                disabled={!isEmpty(errorMessage) || saving}
                onClick={event => ffHandleSubmit(event)}
              />
            ) : (
              <Button
                label="Submit"
                variant="contained"
                color="primary"
                disabled={!isEmpty(errorMessage) || saving}
                onClick={event => ffHandleSubmit(event)}>
                <ActionSend />
              </Button>
            )}

            <Button label="Cancel" variant="outlined" color="primary" onClick={() => onDrawerClose()} style={{ marginLeft: "1em" }}>
              <ActionClose />
            </Button>
          </div>
        </div>
      </MuiDrawer>
    </div>
  );
};

export default RollRemoveDrawer;
